import { Loader as MapsAPILoader } from "@googlemaps/js-api-loader";
import { TextDisplay } from "./text.js";

import { Sharawadji } from "./sharawadji/index.js";
import initHUD, { setLatLngDisplay, setPovDisplay } from "./hud.js";
import { resetGame } from "./reset.js";
import {
  LOCALSTORAGE_POSITION_KEY,
  START_POSITION,
  START_POV,
  MAPS_API_KEY,
} from "./constants.js";
import loadItems from "./items/index.js";
import { completed as completedChapters } from "./chapters.js";
import initGamepad from "./gamepad.js";
import { initTask } from "./task.js";
import { initSpeech } from "./script/speech.js";
import { localisedSounds } from "./audio/localised-sounds.js";
import { enableClickToGoCB, enableSFX } from "./script/utils.js";
import { checkForCheckpoints } from "./checkpoints.js";
import createSFX from "./audio/sfx.js";
import createScore from "./audio/score-sounds.js";
import createSoundscape from "./audio/soundscapes.js";
import { initCustomPanorama } from "./custom-panorama/index.js";
import { LIMBO_LNG_STEP } from "./custom-panorama/limbo.js";
import { checkSafari } from "./safari.js";
import { button as cruiseControlButton } from "./cruise-control.js";
import { initPanoRecovery } from "./pano-recovery.js";
import { startCollab } from "./collab.js";

const container = document.getElementById("container");
const intro = document.getElementById("intro");
const introCTA = document.getElementById("intro-cta");
const introCTAContinue = document.getElementById("intro-cta-continue");

const textContainer = document.getElementById("text-display");
const statusContainer = document.getElementById("status-display");
const helpContainer = document.getElementById("help-display");
const textDisplay = new TextDisplay(textContainer);

const currentURL = new URL(location.href);
const debug = currentURL.searchParams.get("debug") === "true";
const dev = true;
const cinema = currentURL.searchParams.get("cinema") === "true";
const demo = currentURL.searchParams.get("demo") === "true";

const scriptContext = {
  demo,
  container,
  statusContainer,
  helpContainer,
  textDisplay,
};

document.body.classList.toggle("debug", debug);
document.body.classList.toggle("dev", dev);
document.body.classList.toggle("cinema", cinema);
document.body.classList.toggle("demo", demo);

const initialPosition =
  JSON.parse(localStorage.getItem(LOCALSTORAGE_POSITION_KEY)) || START_POSITION;

const mapOptions = {
  position: initialPosition,
  pov: START_POV,
  clickToGo: debug ? true : false,
  disableDefaultUI: true,
  showRoadLabels: false,
};

if (initialPosition.lat === 0) {
  // we're in limbo
  delete mapOptions.position;
  mapOptions.pano = `limbo${Math.round(initialPosition.lng * LIMBO_LNG_STEP)}`;
  mapOptions.pov = { heading: 80, pitch: 0, zoom: 1 };
} else if (initialPosition.lat === 1) {
  delete mapOptions.position;
  mapOptions.pano = `exitLimbo${Math.round(initialPosition.lng * LIMBO_LNG_STEP)}`;
  mapOptions.pov = { heading: 80, pitch: 0, zoom: 1 };
}

document.documentElement.style = `--intro-darkness-duration: 0ms;`;
introCTAContinue.hidden = false;

const initialize = async () => {
  if (checkSafari()) {
    return;
  }

  initHUD(scriptContext);

  const mapsLoader = new MapsAPILoader({
    apiKey: MAPS_API_KEY,
    version: "3.exp",
    libraries: ["streetView"],
  });

  const { StreetViewPanorama, InfoWindow, StreetViewService } =
    await mapsLoader.importLibrary("streetView");
  const { event, ControlPosition, Size, LatLng } =
    await mapsLoader.importLibrary("core");
  const { Map, MapTypeId } = await mapsLoader.importLibrary("maps");
  scriptContext.google = {
    event,
    StreetViewPanorama,
    StreetViewService,
    InfoWindow,
    Size,
    LatLng,
    Map,
    MapTypeId,
  };

  container.hidden = false;

  ((mapOptions.panControl = true),
    (mapOptions.panControlOptions = {
      position: ControlPosition.BLOCK_START_INLINE_END,
    }));

  const map = new StreetViewPanorama(container, mapOptions);
  window.map = map;
  window.scriptContext = scriptContext;
  scriptContext.map = map;

  await initCustomPanorama(scriptContext);

  intro.removeEventListener("click", initialize);
  intro.hidden = true;

  scriptContext.audioContext = new AudioContext();

  scriptContext.masterGain = new GainNode(scriptContext.audioContext, {
    gain: 0,
  });
  scriptContext.masterGain.connect(scriptContext.audioContext.destination);

  scriptContext.masterGain.connect(scriptContext.audioContext.destination);

  scriptContext.speechGain = new GainNode(scriptContext.audioContext, {
    gain: 0.9,
  });
  scriptContext.speechGain.connect(scriptContext.masterGain);

  scriptContext.score = createScore(scriptContext);
  scriptContext.soundscape = createSoundscape(scriptContext);

  scriptContext.sfx = await createSFX(scriptContext);

  scriptContext.masterGain.gain.setValueAtTime(
    1,
    scriptContext.audioContext.currentTime,
  );

  scriptContext.localisedSounds = new Sharawadji(
    localisedSounds,
    scriptContext.map,
    {
      debug,
      compressor: true,
    },
  );

  if (!debug && completedChapters.size === 0) {
    initialSequence(scriptContext);
  } else {
    revisitedSequence(scriptContext);
  }

  if (!map.getPosition()) {
    console.info("[main]", "map does not have position");
    map.setPosition(START_POSITION);
  }

  window.teleportToChile = () => {
    console.log("teleporting");
    map.setPosition({
      lat: -20.22333,
      lng: -69.78822,
    });
  };
  window.backToStart = () => {
    console.log("backToStart");
    map.setPosition(START_POSITION);
  };

  scriptContext.items = await loadItems(InfoWindow, scriptContext);
  scriptContext.items.forEach((o) => o.update());

  checkForCheckpoints(scriptContext)();

  event.addListener(map, "position_changed", () => {
    checkForCheckpoints(scriptContext)();
    scriptContext.sfx.footsteps();

    const position = {
      lat: map.getPosition().lat(),
      lng: map.getPosition().lng(),
    };
    setLatLngDisplay(position);

    Array.from(document.getElementsByTagName("video")).map((el) => {
      if (el.playing || !el.autoplay) return;

      el.play();
    });

    localStorage.setItem(LOCALSTORAGE_POSITION_KEY, JSON.stringify(position));
    scriptContext.items.forEach((o) => o.update());
  });

  event.addListener(map, "pov_changed", () => {
    const pov = map.getPov();
    setPovDisplay(pov);

    scriptContext.items.forEach((o) => o.povUpdate?.());
  });

  initGamepad(scriptContext);
  setLatLngDisplay(initialPosition);
  setPovDisplay(map.getPov());
  initPanoRecovery(scriptContext);
  startCollab(scriptContext);

  document.body.classList.add("game-on");
};

const initialSequence = (context) => {
  console.info("[main]", "initial sequence");
  enableSFX(context);
  enableClickToGoCB(context);

  document.documentElement.style = `--intro-darkness-duration: 0ms;`;

  context.soundscapeGain.gain.setValueAtTime(
    0,
    context.audioContext.currentTime,
  );
  context.soundscapeGain.gain.linearRampToValueAtTime(
    1,
    context.audioContext.currentTime + 2,
  );
};

const revisitedSequence = (context) => {
  console.info("[main]", "revisited sequence");

  enableSFX(context);
  enableClickToGoCB(context);
  context.soundscapeGain.gain.setValueAtTime(
    0,
    context.audioContext.currentTime,
  );
  context.soundscapeGain.gain.linearRampToValueAtTime(
    1,
    context.audioContext.currentTime + 2,
  );
  cruiseControlButton.hidden = false;
};

let startGamepadPoll;

const gamepadStarter = () => {
  if (scriptContext.map) return;

  startGamepadPoll = requestAnimationFrame(gamepadStarter);

  const gamepad = navigator.getGamepads()[0];
  if (!gamepad) {
    return;
  }

  if (
    !speedrunVideo.paused &&
    gamepad.buttons.some((button) => button.value > 0)
  ) {
    location.reload();
  }
};

gamepadStarter();

introCTA.addEventListener("click", () => {
  initialPosition.lat = START_POSITION.lat;
  initialPosition.lng = START_POSITION.lng;
  initialize();
});

introCTAContinue.addEventListener("click", initialize);
